import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net';
import axios from 'axios';
import 'datatables.net-buttons';
import 'datatables.net-buttons-dt/css/buttons.dataTables.min.css';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import Swal from 'sweetalert2';
import $ from 'jquery';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { baseurl } from '../utility/helper';

const TestList = () => {
  const roleid         = parseInt(sessionStorage.getItem("userroleId"));
  const userChapter    = sessionStorage.getItem("userchapterId") || "";
  const chaptername    = sessionStorage.getItem("userchaptername");
  const username       = sessionStorage.getItem("username");
  const mobileno       = sessionStorage.getItem("mobileno");
  const token          = sessionStorage.getItem("token");
  const initialChapter = roleid === 2 ? sessionStorage.getItem("userchapterId") || "" : "";
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [allTests,       setAllTests]       = useState([]);
  const [ongoingTests,   setOngoingTests]   = useState([]);   // scheduled, not started yet
  const [pendingTests,   setPendingTests]   = useState([]);   // test done, awaiting results
  const [completedTests, setCompletedTests] = useState([]);   // results + certificate done

  const [totalTest,      setTotal]         = useState(0);
  const [ongoingCount,   setOngoingCount]  = useState(0);
  const [pendingCount,   setPendingCount]  = useState(0);
  const [completedCount, setCompletedCount]= useState(0);
  const [ongoingPct,     setOngoingPct]    = useState(0);
  const [pendingPct,     setPendingPct]    = useState(0);
  const [completedPct,   setCompletedPct]  = useState(0);

  // 'all' | 'ongoing' | 'pending' | 'completed'
  const [activeCard, setActiveCard] = useState('all');

  const ongoingRef   = useRef(null);
  const pendingRef   = useRef(null);
  const completedRef = useRef(null);
  const navigate     = useNavigate();

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDateTime = (timestamp, type = 'display') => {
    if (!timestamp) return type === 'display' ? '' : 0;

    const date = new Date(timestamp);

    // For sort/type/filter, return a numeric timestamp so ordering is chronological
    if (type !== 'display') {
      return date.getTime();
    }

    const day     = String(date.getDate()).padStart(2, '0');
    const month   = String(date.getMonth() + 1).padStart(2, '0');
    const year    = date.getFullYear();
    let hours     = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm    = hours >= 12 ? 'PM' : 'AM';
    hours         = hours % 12 || 12;
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  /**
   * Three-way category logic:
   *
   *  'ongoing'   — test_start_time is in the future (not yet happened)
   *  'pending'   — test has happened (test_end_time passed) but test_status ≠ 'completed'
   *  'completed' — test_status === 'completed'
   *
   * Adjust the field names / conditions below if your API uses different
   * field names or a different status string for "completed".
   */
  const getTestCategory = (test) => {
    if (test.test_status === 'completed') return 'completed';

    const now      = new Date();
    const testEnd  = new Date(test.test_end_time);

    // Test hasn't finished yet → ongoing (scheduled / in-progress)
    if (testEnd >= now) return 'ongoing';

    // Test finished but result not yet published → pending
    return 'pending';
  };

  // ── Fetch all tests ────────────────────────────────────────────────────────
  const fetchTests = async () => {
    try {
      if (!token) return;
      let url = `${baseurl}/admin/tests`;
      if (roleid === 2 && initialChapter) url += `?chapterId=${initialChapter}`;

      const response = await fetch(url, {
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      setAllTests(data);
      applyFilter(data, 'all');
      computeCounts(data);
    } catch (err) {
      console.error('Error fetching tests:', err);
    }
  };

  // ── Compute summary counts ─────────────────────────────────────────────────
  const computeCounts = (data) => {
    const total     = data.length;
    const ongoing   = data.filter(t => getTestCategory(t) === 'ongoing').length;
    const pending   = data.filter(t => getTestCategory(t) === 'pending').length;
    const completed = data.filter(t => getTestCategory(t) === 'completed').length;

    setTotal(total);
    setOngoingCount(ongoing);
    setPendingCount(pending);
    setCompletedCount(completed);
    setOngoingPct(  total > 0 ? (ongoing   / total) * 100 : 0);
    setPendingPct(  total > 0 ? (pending   / total) * 100 : 0);
    setCompletedPct(total > 0 ? (completed / total) * 100 : 0);
  };

  // ── Apply card filter ──────────────────────────────────────────────────────
  const applyFilter = (data, filter) => {
    setActiveCard(filter);

    const ongoing   = data.filter(t => getTestCategory(t) === 'ongoing');
    const pending   = data.filter(t => getTestCategory(t) === 'pending');
    const completed = data.filter(t => getTestCategory(t) === 'completed');

    if (filter === 'all') {
      setOngoingTests(ongoing);
      setPendingTests(pending);
      setCompletedTests(completed);
      return;
    }
    if (filter === 'ongoing') {
      setOngoingTests(ongoing);
      setPendingTests([]);
      setCompletedTests([]);
      return;
    }
    if (filter === 'pending') {
      setOngoingTests([]);
      setPendingTests(pending);
      setCompletedTests([]);
      return;
    }
    if (filter === 'completed') {
      setOngoingTests([]);
      setPendingTests([]);
      setCompletedTests(completed);
      return;
    }
  };

  const handleCardClick = (filter) => {
    setLoadingFilter(true);

    Swal.fire({
      title: "Please wait...",
      text: "Filtering tests...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    applyFilter(allTests, filter);
  };

  const renderTable = (tableElement, tableData, statusBadge) => {
    if (!tableElement) return;

    if ($.fn.DataTable.isDataTable(tableElement)) {
      const table = $(tableElement).DataTable();
      table.clear();
      table.rows.add(tableData);
      table.draw();
      return;
    }

    $(tableElement).DataTable({
      dom: 'Blfrtip',
      pageLength: 25,
      lengthMenu: [[10, 25, 50, 100, 250, 500, -1], [10, 25, 50, 100, 250, 500, "All"]],
      buttons: [
        { extend: 'copyHtml5',  className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'excelHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'csvHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'pdfHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'print',      className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
      ],
      data: tableData,
      columns: [
        { data: 'test_id',             title: 'Test ID' },
        { data: 'chapter_id',          title: 'Chapter ID' },
        { data: 'test_start_time',     title: 'Start Time',     render: (d, type) => formatDateTime(d, type) },
        { data: 'test_end_time',       title: 'End Time',       render: (d, type) => formatDateTime(d, type) },
        { data: 'test_title',          title: 'Title' },
        {
          data: null,
          title: statusBadge === 'completed' ? 'Pass/Register' : 'Seat',
          render: (row, type) => {
            const registered = row.registered_count ?? 0;

            if (statusBadge === 'completed') {
              const passed = row.passed_count ?? 0;   // ← needs backend support
              return type === 'display' ? `${passed}/${registered}` : passed;
            }

            const total = row.seat ?? '-';   // ← rename to your real column
            return type === 'display' ? `${registered}/${total}` : registered;
          },
        },

        { data: 'venue',               title: 'Venue' },
        { data: 'register_start_date', title: 'Register Start', render: (d, type) => formatDateTime(d, type) },
        { data: 'register_end_date',   title: 'Register End',   render: (d, type) => formatDateTime(d, type) },
        { data: null,                  title: 'Status' },
      ],
      columnDefs: [
        {
          targets: 0,
          render: (data, type, row) =>
            `<a href="#" class="edit-link" data-id="${data}" data-chapterid="${row.chapter_id}">${data}</a>`,
        },
        {
          targets: 9,
          render: (data, type, row) => {
            if (statusBadge === 'completed')
              return `<span class="badge bg-success">Completed</span>`;
            if (statusBadge === 'pending')
              return `<span class="badge bg-warning text-dark">Result Pending</span>`;
            // ongoing
            return `<span class="badge bg-primary">Ongoing</span>`;
          },
        },
      ],
      order: [[0, 'desc']],
      initComplete: function () {
        $('.dt-length select').addClass('form-select form-select-sm');
      }
    });
  };

  // ── Init ────
  useEffect(() => {
    fetchTests();
  }, []);

  // ── Re-render tables when data changes ────────────────────────────────────
  useEffect(() => {
    renderTable(ongoingRef.current,   ongoingTests,   'ongoing');
    renderTable(pendingRef.current,   pendingTests,   'pending');
    renderTable(completedRef.current, completedTests, 'completed');

    $(document).off('click', '.edit-link').on('click', '.edit-link', function (e) {
        e.preventDefault();

        const testId = $(this).data('id');
        const chapterId = $(this).data('chapterid');

        const url = `/admin/EditTest?testId=${testId}&ChapterID=${chapterId}`;

        window.open(url, '_blank');
    });

    $(document).off('click', '.edit-icon').on('click', '.edit-icon', function (e) {
      e.preventDefault();
      navigate('/admin/EditTest', { state: { testId: $(this).data('id'), edit: true } });
    });

    $(document).off('click', '.delete-icon').on('click', '.delete-icon', function (e) {
      e.preventDefault();
      const testId = $(this).data('id');
      Swal.fire({
        title: 'Are you sure?', text: "You won't be able to revert this!",
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: '#3085d6', cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          $.ajax({
            url: `${baseurl}/admin/tests/${testId}`, type: 'DELETE',
            headers: { Authorization: token, 'Content-Type': 'application/json' },
            success: (res) => Swal.fire('Deleted!', res.message, 'success').then(fetchTests),
            error:   (xhr) => Swal.fire('Error!', xhr.responseJSON?.message || 'Error deleting test.', 'error'),
          });
        }
      });
    });
  }, [ongoingTests, pendingTests, completedTests]);

  if (loadingFilter) {
    Swal.close();
    setLoadingFilter(false);
  }
  // ── Card style helper ──────────────────────────────────────────────────────
  const cardStyle = (filter) => ({
    cursor: 'pointer',
    border: activeCard === filter ? '2px solid #0d6efd' : '1px solid #dee2e6',
    boxShadow: activeCard === filter ? '0 0 0 3px rgba(13,110,253,.2)' : 'none',
    transition: 'box-shadow 0.2s, border 0.2s',
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="content container-fluid">

      {/* Header bar */}
      <div className="row bg-white p-3 rounded shadow-sm align-items-center mb-3">
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <p className="mb-0">
            <span className="fw-semibold text-secondary">Chapter</span> {userChapter}: <b>{chaptername}</b>
          </p>
        </div>
        <div className="col-12 col-md-4 text-center mb-2 mb-md-0">
          {roleid === 1 && <div><span className="user-details">Chapter SuperAdmin: </span><b>{username}</b></div>}
          {roleid === 2 && <div><span className="user-details">Chapter Admin: </span><b>{username}</b></div>}
        </div>
        <div className="col-12 col-md-4 text-md-end">
          <p className="mb-0"><span className="fw-semibold text-secondary">Mobile No:</span> <b>{mobileno}</b></p>
        </div>
      </div>

      {/* Summary cards — now 4 cards in a row (Total + 3 categories) */}
      <div className="row">

        {/* Total Tests */}
        <div className="col-xl-3 col-sm-6 col-12">
          <div className="card mb-2" style={cardStyle('all')} onClick={() => handleCardClick('all')}>
            <div className="card-body">
              <div className="dash-widget-header">
                <span className="dash-widget-icon bg-1"><img src="/assets/img/favicon-32x32.png" alt="icon" /></span>
                <div className="dash-count"><div className="dash-title"><p>Total Tests</p><h3>{totalTest}</h3></div></div>
              </div>
              <div className="progress progress-sm mt-3">
                <div className="progress-bar bg-5" role="progressbar" style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Ongoing Tests */}
        <div className="col-xl-3 col-sm-6 col-12">
          <div className="card mb-2" style={cardStyle('ongoing')} onClick={() => handleCardClick('ongoing')}>
            <div className="card-body">
              <div className="dash-widget-header">
                <span className="dash-widget-icon bg-1"><img src="/assets/img/favicon-32x32.png" alt="icon" /></span>
                <div className="dash-count"><div className="dash-title"><p>Ongoing Tests</p><h3>{ongoingCount}</h3></div></div>
              </div>
              <div className="progress progress-sm mt-3">
                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${ongoingPct}%` }} aria-valuenow={ongoingPct} aria-valuemin="0" aria-valuemax="100" />
              </div>
            </div>
          </div>
        </div>

        {/* Result Pending */}
        <div className="col-xl-3 col-sm-6 col-12">
          <div className="card mb-2" style={cardStyle('pending')} onClick={() => handleCardClick('pending')}>
            <div className="card-body">
              <div className="dash-widget-header">
                <span className="dash-widget-icon bg-1"><img src="/assets/img/favicon-32x32.png" alt="icon" /></span>
                <div className="dash-count"><div className="dash-title"><p>Result Pending Tests</p><h3>{pendingCount}</h3></div></div>
              </div>
              <div className="progress progress-sm mt-3">
                <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${pendingPct}%` }} aria-valuenow={pendingPct} aria-valuemin="0" aria-valuemax="100" />
              </div>
            </div>
          </div>
        </div>

        {/* Completed Tests */}
        <div className="col-xl-3 col-sm-6 col-12">
          <div className="card mb-2" style={cardStyle('completed')} onClick={() => handleCardClick('completed')}>
            <div className="card-body">
              <div className="dash-widget-header">
                <span className="dash-widget-icon bg-1"><img src="/assets/img/favicon-32x32.png" alt="icon" /></span>
                <div className="dash-count"><div className="dash-title"><p>Completed Tests</p><h3>{completedCount}</h3></div></div>
              </div>
              <div className="progress progress-sm mt-3">
                <div className="progress-bar bg-success" role="progressbar" style={{ width: `${completedPct}%` }} aria-valuenow={completedPct} aria-valuemin="0" aria-valuemax="100" />
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Tables */}
      <div className="container mt-4">
        <div className="profile-section">

          <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
            <button style={{ border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}>
              <Link to="/admin/Test" style={{ display: 'block', padding: '0.5rem 1rem', textDecoration: 'none', color: 'inherit' }}>
                <i className="bx bxs-plus-circle mr-2"></i> Add Test
              </Link>
            </button>
          </div>

          {/* ── Ongoing Tests table ── */}
          {(activeCard === 'all' || activeCard === 'ongoing') && (
            <div className="list mb-4">
              <div className="row">
                <center><h4><b>Ongoing Tests</b></h4></center>
                <div className="col-12" style={{ overflow: 'auto' }}>
                  <table ref={ongoingRef} className="table table-striped table-bordered" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Test ID</th><th>Start Time</th><th>End Time</th>
                        <th>Title</th><th>Venue</th>
                        <th>Register Start</th><th>Register End</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody></tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {(activeCard === 'all') && ongoingTests.length > 0 && pendingTests.length > 0 && (
            <><br /><hr /><br /></>
          )}

          {/* ── Result Pending Tests table ── */}
          {(activeCard === 'all' || activeCard === 'pending') && (
            <div className="list mb-4">
              <div className="row">
                <center><h4><b>Result Pending Tests</b></h4></center>
                <div className="col-12" style={{ overflow: 'auto' }}>
                  <table ref={pendingRef} className="table table-striped table-bordered" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Test ID</th><th>Start Time</th><th>End Time</th>
                        <th>Title</th><th>Venue</th>
                        <th>Register Start</th><th>Register End</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody></tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {(activeCard === 'all') && pendingTests.length > 0 && completedTests.length > 0 && (
            <><br /><hr /><br /></>
          )}

          {/* ── Completed Tests table ── */}
          {(activeCard === 'all' || activeCard === 'completed') && (
            <div className="list">
              <div className="row">
                <center><h4><b>Completed Tests</b></h4></center>
                <div className="col-12" style={{ overflow: 'auto' }}>
                  <table ref={completedRef} className="table table-striped table-bordered" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Test ID</th><th>Start Time</th><th>End Time</th>
                        <th>Title</th><th>Venue</th>
                        <th>Register Start</th><th>Register End</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody></tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TestList;